'use client';

import { useEffect, useState } from 'react';
import type { VariantNudge } from '@/types/concept';

/**
 * Delay before the first-design nudge is allowed on screen, in milliseconds.
 *
 * Right after a render lands, the finished image is the ONLY thing the user
 * looks at — it is by far the strongest attention magnet on the page. A banner
 * that appears in that same instant loses the competition, and because the user
 * technically "saw" it, repeat blindness means they will not read it later
 * either. That is why the old teal callout was reliably skipped: people went
 * straight from the image to editing settings in Refine mode while actually
 * wanting a brand-new design.
 *
 * Holding the callout back a few seconds lets the image land first, so the
 * banner arrives into a free attention slot instead of a busy one. The same
 * constant is mirrored on Apple (`VariantNudgeTiming.appearDelay`) and Android
 * (`VariantNudgeTiming.APPEAR_DELAY_MS`) — keep the three in sync.
 */
export const NUDGE_APPEAR_DELAY_MS = 5000;

/**
 * Nudge types whose appearance is delayed. Only `choose_path` fires immediately
 * after a render completes, so only it collides with the image reveal. The other
 * types surface later in a session, when nothing else is competing for
 * attention, and delaying them would just make them feel laggy.
 */
const DELAYED_TYPES = new Set(['choose_path']);

/**
 * Gates a server-driven variant nudge behind a short delay.
 *
 * Returns the nudge once it is allowed on screen, `null` while it is still held
 * back. The timer restarts whenever the nudge identity changes (type flip, or a
 * different design), so a nudge never inherits an already-elapsed delay from a
 * previous one.
 */
export function useDelayedNudge(
  nudge: VariantNudge | null | undefined,
  /**
   * Identity of the thing the nudge is attached to (e.g. the render id). A
   * change re-arms the delay — without it, navigating between designs would
   * show the callout instantly because the timer had already fired.
   */
  contextKey: string | null | undefined
): VariantNudge | null {
  const isDelayed = Boolean(nudge?.type && DELAYED_TYPES.has(nudge.type));
  const [isElapsed, setIsElapsed] = useState(false);

  useEffect(() => {
    if (!isDelayed) {
      return;
    }

    setIsElapsed(false);
    const timer = window.setTimeout(
      () => setIsElapsed(true),
      NUDGE_APPEAR_DELAY_MS
    );

    return () => window.clearTimeout(timer);
  }, [isDelayed, nudge?.type, contextKey]);

  if (!nudge) {
    return null;
  }

  return !isDelayed || isElapsed ? nudge : null;
}
